import React from "react";
import { Typography as MuiTypography } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import { usePostHeight } from "../../hooks/usePostHeight";
import ClimateMonthlyChartContainer from "../../components/charts/climateMonthlyChart/ClimateMonthlyChartContainer";
import ClimateMonthlyControls from "./ClimateMonthlyControls";
import { useClimateWaterYears } from "../../hooks/charts/useClimateMonthlySeries";
import { mhfdUnavailable } from "./ClimateMonthly";
import { CLIMATE_PARAM_SHAPE, VARIABLES } from "./climateCopy";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);

const schema = z.object({
  ...CLIMATE_PARAM_SHAPE,
  controls: z.preprocess(
    (value) => value === "true" || value === true,
    z.boolean().default(false)
  ),
  height: z.preprocess(
    (value) => (typeof value === "string" ? parseInt(value, 10) : value),
    z.number().default(400)
  ),
});

export default function ClimateMonthlyEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(schema);
  const { waterYears } = useClimateWaterYears();

  const config = VARIABLES[params.variable] ?? VARIABLES.precip;
  const mhfdDisabled = mhfdUnavailable(params);

  const yearOptions = waterYears.length
    ? waterYears
    : [params.water_year].filter(Boolean);

  const handleFilters = (updater) =>
    setParams((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const waterYear = Number(next.water_year);
      return {
        ...next,
        compare: (next.compare ?? []).filter((year) => year !== waterYear),
      };
    });

  return (
    <main>
      {params.controls && (
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <ClimateMonthlyControls
            params={params}
            onChange={handleFilters}
            waterYears={yearOptions}
          />
        </Section>
      )}

      <Section>
        <Typography variant="h5" component="h2" gutterBottom>
          {config.label} by Water-Year Month, WY{params.water_year}
        </Typography>
        <ClimateMonthlyChartContainer
          variable={params.variable}
          waterYear={params.water_year}
          compareYears={params.compare}
          normalStart={params.normal_start}
          showKapa={params.kapa}
          showMhfd={params.mhfd && !mhfdDisabled}
          units={params.units}
          height={params.height}
          allWaterYears={yearOptions}
          mhfdDisabled={mhfdDisabled}
          onCompareYearsChange={(compare) => setParams({ ...params, compare })}
          onUnitsChange={(units) => setParams({ ...params, units })}
          onShowKapaChange={(show) =>
            setParams({ ...params, kapa: show ? 1 : 0 })
          }
          onShowMhfdChange={(show) =>
            setParams({ ...params, mhfd: show ? 1 : 0 })
          }
        />
      </Section>
    </main>
  );
}
